import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import Grid from "@mui/material/Grid2";
import {
  Button,
  Typography,
  IconButton,
  Box,
  Autocomplete,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { styled } from "@mui/material/styles";
import { green } from "@mui/material/colors";
import { Check, CheckCircleRounded } from "@mui/icons-material";
import {} from "_utils";
import { alertActions } from "_store";
import {
  fileExtension,
  fileSizeReadable,
  fileTypeAcceptable,
} from "_utils";
import { uploadLabels } from "_utils/labels";
import { ModalPopup } from "_components";
import { materialsymbolsupload, materialsymbolsdownload, Delete } from "images";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  getAgencyApi,
  getStateApi,
  supplierFileDownloadApi,
} from "services/supplierDiversity/supplierDiversity.thunks";
import { ERR_MSG } from "constants/labels";
import { Controller, useForm } from "react-hook-form";
import EditIcon from "@mui/icons-material/Edit";

const FileUpload = ({
  portalKey,
  selectedDocumentType,
  multiple = true,
  documentTypes,
  supportedFormats,
  maxFiles = Infinity,
  maxFileSize = 5 * 1024 * 1024,
  minFileSize = 0,
  onFileChange,
  initialFiles = [],
  exsistingFiles = [],
  action = "UPDATE",
  setExpDate,
  setAgency,
  setStateCert,
  agencyID,
  agencyStateID,
  expDate,
  isUserAdmin,
  setUploadFileValid,
  certReplaced,
  setCertReplaced,
  removedFile,
  setRemovedFile,
}) => {
  const header = "Supplier Development";
  const [files, setFiles] = useState(initialFiles);
  const [open, setOpen] = useState(false);
  const [fileToRemove, setFileToRemove] = useState(null);
  const [certState, setCertState] = useState([]);
  const [certAgency, setCertAgency] = useState([]);
  const [isEditExpDate, setIsEditExpDate] = useState(false);
  const [isEditIssue, setIsEditIssue] = useState(false);
  const dispatch = useDispatch();
  const idCounter = useRef(1);
  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  const defaultValues = {
    ExpiryDate: dayjs(),
    StateID: null,
    AgencyID: null,
  };

  const {
    control,
    watch,
    reset,
    resetField,
    formState: { dirtyFields },
  } = useForm({
    mode: "onChange",
    defaultValues,
  });

  const watchExpDate = watch("ExpiryDate");
  const watchState = watch("StateID");
  const watchAgency = watch("AgencyID");

  useEffect(() => {
    setFiles(initialFiles);

    if (removedFile === 4) {
      reset(defaultValues);

      setExpDate(dayjs());
      setAgency(null);
      setStateCert(null);
    } else {
      reset({
        ExpiryDate: expDate,
        StateID: agencyStateID,
        AgencyID: agencyID,
      });
    }
  }, [initialFiles]);

  const convertStateID = (id) => {
    if (certState.length) {
      const getStateLabel = certState.find(
        (state) => state.value === id
      )?.label;

      return getStateLabel;
    } else {
      return "";
    }
  };

  const convertAgencyID = (id) => {
    if (certAgency.length) {
      const getAgencyLabel = certAgency.find(
        (state) => state.value === id
      )?.label;

      return getAgencyLabel;
    } else {
      return "";
    }
  };

  useEffect(() => {
    fetchState();

    if (watchState && watchState !== 0) {
      setStateCert(watchState);
      agencyID = 0;
      resetField("AgencyID", { defaultValue: defaultValues.AgencyID });
      fetchAgency(watchState);
    }

    if (agencyID && agencyStateID) {
      fetchAgency(agencyStateID);
    }
  }, [watchState]);

  const hasValue = (val) => val != null && val !== "";

  useEffect(() => {
    const hasDirtyFields = Object.keys(dirtyFields).length > 0;

    if (watchAgency) {
      setAgency(watchAgency);
    }

    if (watchExpDate) {
      setExpDate(dayjs(watchExpDate).startOf("day").toISOString());
    }

    if (action === "ADD") {
      setUploadFileValid(
        hasValue(watchState) && hasValue(watchAgency) && hasValue(watchExpDate)
      );
    } else {
      setUploadFileValid(
        hasValue(watchState) &&
          hasValue(watchAgency) &&
          hasValue(watchExpDate) &&
          !isEditExpDate &&
          !isEditIssue &&
          (certReplaced || hasDirtyFields)
      );
    }
  }, [watchAgency, watchExpDate, isEditExpDate, isEditIssue]);

  const fetchAgency = async (stateID) => {
    try {
      dispatch(alertActions.clear());
      const res = await dispatch(getAgencyApi(stateID)).unwrap();
      if (res?.Succeeded === false) {
        dispatch(
          alertActions.error({
            message: res?.Message,
            header: header,
          })
        );
        return;
      }
      const sdState = res?.Data?.map((data) => {
        return { label: data?.AgencyName, value: data?.AgencyId };
      });
      setCertAgency(sdState);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header,
        })
      );
    }
  };

  const fetchState = async () => {
    try {
      dispatch(alertActions.clear());
      const res = await dispatch(getStateApi()).unwrap();
      if (res?.Succeeded === false) {
        dispatch(
          alertActions.error({
            message: res?.Message,
            header: header,
          })
        );
        return;
      }
      const sdState = res?.Data?.map((data) => {
        return { label: data?.StateDescription, value: data?.StateId };
      });
      setCertState(sdState);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header,
        })
      );
    }
  };

  const handleChange = async (event, overrideDocumentType = null) => {
    event.preventDefault();
    let filesAdded = event.dataTransfer
      ? event.dataTransfer.files
      : event.target.files;

    const activeDocumentType = overrideDocumentType || selectedDocumentType;

    if (
      documentTypes &&
      documentTypes.length > 0 &&
      !activeDocumentType &&
      action !== "UPDATE"
    ) {
      dispatch(
        alertActions.error({
          message: "Please select any Document Type",
          header: "Validation Error",
        })
      );
      return;
    }

    if (multiple === false && filesAdded.length > 1) {
      filesAdded = [filesAdded[0]];
    }

    const fileResults = [];
    for (let i = 0; i < filesAdded.length; i += 1) {
      const file = filesAdded[i];

      file.DocumentTypeID =
        documentTypes && documentTypes.length > 0
          ? activeDocumentType
          : `files-${idCounter.current}`;

      file.extension = fileExtension(file);
      file.sizeReadable = fileSizeReadable(file.size);

      if (file.size > maxFileSize) {
        dispatch(
          alertActions.error({
            message: `File size cannot exceed 5MB`,
            header: `File Upload`,
          })
        );
        break;
      }

      if (file.size < minFileSize) {
        dispatch(
          alertActions.error({
            message: `${file.name} is too small`,
            header: `File Upload`,
          })
        );
        break;
      }

      if (!fileTypeAcceptable(supportedFormats, file)) {
        dispatch(
          alertActions.error({
            message: `Invalid file format.`,
            header: `File Upload`,
          })
        );
        break;
      }

      const oldFileData = exsistingFiles?.find(
        (f) => f.DocumentTypeID === file.DocumentTypeID
      );
      const fileData = {
        ID: oldFileData?.ID || null, // This will be updated if replacing an existing file
        AdditionalID: oldFileData?.AdditionalID || 0,
        DocumentTypeID: file.DocumentTypeID,
        FileName: file.name,
        Format: file.extension,
        Size: file.size,
        PortalKey: portalKey, // Replace with actual portal key if needed
        File: file,
        Url: null,
      };

      fileResults.push(fileData);
      setRemovedFile(fileData.DocumentTypeID);
      action === "UPDATE" && setCertReplaced(true);
    }

    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.map((prevFile) => {
        const newFile = fileResults.find(
          (newFile) => newFile.DocumentTypeID === prevFile.DocumentTypeID
        );
        return newFile
          ? {
              ...newFile,
              ID: prevFile.ID,
              AdditionalID: prevFile.AdditionalID || 0,
              Url: null,
            }
          : prevFile;
      });

      const newUniqueFiles = fileResults.filter(
        (newFile) =>
          !prevFiles.some(
            (prevFile) => prevFile.DocumentTypeID === newFile.DocumentTypeID
          )
      );

      const newFiles = [...updatedFiles, ...newUniqueFiles];
      onFileChange(newFiles);
      return newFiles;
    });
  };

  const handleFileRemove = (documentTypeId) => {
    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter(
        (prevFile) => prevFile.DocumentTypeID !== documentTypeId
      );

      const removedFiles = prevFiles.filter(
        (prevFile) => prevFile.DocumentTypeID === documentTypeId
      );

      setRemovedFile(removedFiles.DocumentTypeID);
      onFileChange(updatedFiles); // Notify parent component of file changes
      return updatedFiles;
    });
    setOpen(false);
    setFileToRemove(null); // Reset fileToRemove state
    action === "UPDATE" && setCertReplaced(true);
    reset(defaultValues);
  };

  const handleDialogOpen = (documentTypeId) => {
    setFileToRemove(documentTypeId);
    setOpen(true);
  };

  const handleDialogClose = () => {
    setOpen(false);
    setFileToRemove(null); // Reset fileToRemove state
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Handle file input change
    }
  };

  const handleDownload = async (file) => {
    try {
      const { Url: fileName, File: localFile } = file;
      if (fileName) {
        await dispatch(supplierFileDownloadApi(fileName)).unwrap();
      } else if (localFile) {
        const url = URL.createObjectURL(localFile);
        const link = document.createElement("a");

        link.href = url;
        link.download = localFile?.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        throw new Error(ERR_MSG["073"]);
      }
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        }),
      );
    }
  };

  return (
    <Typography component="div" className="UploadContainer">
      <Grid container spacing={3}>
        {!isUserAdmin ? (
          <>
            <Grid item xs={12} sm={12} md={12} width={"100%"}>
              <Button
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                className={`Uploadfiles ${
                  !selectedDocumentType ? "disabled" : ""
                }`}
                sx={{
                  height: "190px !important",
                  width: "100% !important",
                }}
              >
                <Box
                  component="p"
                  variant="body1"
                  sx={{
                    textTransform: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontFamily: "Proxima Nova",
                  }}
                >
                  <img
                    src={materialsymbolsupload}
                    alt="Upload"
                    width={24}
                    height={24}
                  />
                  <Typography
                    component="span"
                    sx={{ fontWeight: "700 !important" }}
                  >
                    Upload your files here
                  </Typography>
                </Box>
                <span
                  type="file"
                  onChange={handleFileInputChange}
                  className="Browsechoose supplier-diversity"
                >
                  Browse and choose the file(s) you want to upload
                </span>
                <VisuallyHiddenInput
                  type="file"
                  onChange={handleChange}
                  disabled={!selectedDocumentType}
                />
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={12}>
              <Typography component="div" className="SupportedFormats">
                <Typography component="h3" sx={{ marginTop: "0px !important" }}>
                  Supported Formats
                </Typography>
                <Typography component="div" className="fileformatlist">
                  {supportedFormats &&
                    supportedFormats.map((format) => (
                      <span key={format}>{format}</span>
                    ))}
                </Typography>
              </Typography>
            </Grid>
          </>
        ) : (
          ""
        )}
        {files.length ? (
          <Grid item xs={12} sm={6} md={12}>
            <Typography
              component="div"
              className="SupportedFormats Personal-Informationsheading"
            >
              <Typography component="h2">Uploaded Documents</Typography>
              <Typography component="div" className="fileformat">
                {documentTypes &&
                  documentTypes.map((type) => {
                    const uploadedDocument = files.filter(
                      (x) => x.DocumentTypeID === type.value
                    );
                    if (uploadedDocument && uploadedDocument.length > 0) {
                      return (
                        <Box>
                          {action === "ADD" ? (
                            <div className="mar-top-16" key={type.value}>
                              <Typography component="div">
                                <CheckCircleRounded
                                  fontSize="small"
                                  style={{ color: green[500] }}
                                />
                                <Typography
                                  component="p"
                                  className="DocumentDescription"
                                >
                                  {type.label}
                                </Typography>
                                <Typography
                                  component="div"
                                  className="DocumentTypeID"
                                >
                                  <Grid container>
                                    <Typography className="textwordwrap">
                                      {uploadedDocument[0].FileName}
                                    </Typography>
                                    <IconButton
                                      onClick={() =>
                                        handleDownload(
                                          uploadedDocument[0],
                                        )
                                      }
                                    >
                                      <img
                                        src={materialsymbolsdownload}
                                        alt="download"
                                      ></img>
                                    </IconButton>
                                    <IconButton
                                      onClick={() =>
                                        handleDialogOpen(type.value)
                                      }
                                    >
                                      <img src={Delete} alt="Delete"></img>
                                    </IconButton>
                                  </Grid>
                                  {uploadedDocument[0].DocumentTypeID === 4 ? (
                                    <Grid container flexDirection={"column"}>
                                      <Grid item xs={12}>
                                        <Typography
                                          fontSize={"14px"}
                                          fontWeight={"400"}
                                        >
                                          Expires on:{" "}
                                          {watchExpDate
                                            ? dayjs(watchExpDate).format(
                                                "MM/DD/YYYY"
                                              )
                                            : dayjs(expDate).format(
                                                "MM/DD/YYYY"
                                              )}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Typography
                                          fontSize={"14px"}
                                          fontWeight={"400"}
                                        >
                                          Issued by:{" "}
                                          {watchAgency
                                            ? convertAgencyID(watchAgency)
                                            : convertAgencyID(agencyID)}{" "}
                                          (
                                          {watchState
                                            ? convertStateID(watchState)
                                            : convertStateID(agencyStateID)}
                                          )
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  ) : (
                                    ""
                                  )}
                                </Typography>
                              </Typography>
                            </div>
                          ) : (
                            <Box
                              sx={{
                                mt: 2,
                                backgroundColor: "#FFFFFF",
                                padding: 2,
                              }}
                              key={type.value}
                            >
                              <Typography
                                component="div"
                                display={"flex"}
                                flexDirection={"column"}
                              >
                                {uploadedDocument.length > 0
                                  ? uploadedDocument.map((doc, index) => (
                                      <>
                                        <Typography fontWeight={700}>
                                          {type.label}
                                        </Typography>
                                        <Typography
                                          key={index}
                                          component="div"
                                          style={{ marginBottom: "8px" }}
                                        >
                                          <Grid container>
                                            <Typography
                                              className="textwordwrap"
                                              style={{ flex: 1 }}
                                            >
                                              {doc.FileName}
                                            </Typography>
                                            <div>
                                              <IconButton
                                                onClick={() =>
                                                  handleDownload(doc)
                                                }
                                              >
                                                <img
                                                  src={materialsymbolsdownload}
                                                  alt="download"
                                                />
                                              </IconButton>
                                              {!isUserAdmin ? (
                                                <IconButton
                                                  onClick={() =>
                                                    handleDialogOpen(type.value)
                                                  }
                                                >
                                                  <img
                                                    src={Delete}
                                                    alt="Delete"
                                                  ></img>
                                                </IconButton>
                                              ) : (
                                                ""
                                              )}
                                            </div>
                                          </Grid>
                                        </Typography>
                                        {type.value === 4 ? (
                                          <Grid container spacing={1}>
                                            <Grid
                                              item
                                              xs={12}
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "16px",
                                                width: "100%",
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 2,
                                                }}
                                              >
                                                <Typography
                                                  component="span"
                                                  fontWeight="bold"
                                                >
                                                  Expires on:
                                                </Typography>

                                                {isEditExpDate ? (
                                                  <Controller
                                                    name="ExpiryDate"
                                                    control={control}
                                                    rules={{
                                                      required:
                                                        "Expiration date is required",
                                                    }}
                                                    render={({
                                                      field,
                                                      fieldState,
                                                    }) => (
                                                      <LocalizationProvider
                                                        dateAdapter={
                                                          AdapterDayjs
                                                        }
                                                      >
                                                        <DatePicker
                                                          label="Expires On"
                                                          value={
                                                            field.value
                                                              ? dayjs(
                                                                  field.value
                                                                )
                                                              : null
                                                          }
                                                          onChange={(
                                                            newValue
                                                          ) =>
                                                            field.onChange(
                                                              newValue
                                                                ? newValue.toISOString()
                                                                : null
                                                            )
                                                          }
                                                          minDate={dayjs()}
                                                          slotProps={{
                                                            textField: {
                                                              error:
                                                                !!fieldState.error,
                                                              helperText:
                                                                fieldState.error
                                                                  ?.message,
                                                            },
                                                          }}
                                                        />
                                                      </LocalizationProvider>
                                                    )}
                                                  />
                                                ) : (
                                                  <Typography
                                                    component="span"
                                                    fontWeight="bold"
                                                  >
                                                    {dayjs(expDate).format(
                                                      "MM/DD/YYYY"
                                                    )}
                                                  </Typography>
                                                )}
                                              </Box>
                                              {!isUserAdmin ? (
                                                isEditExpDate ? (
                                                  <Check
                                                    fontSize="small"
                                                    onClick={() =>
                                                      setIsEditExpDate(false)
                                                    }
                                                    style={{
                                                      cursor: "pointer",
                                                      color: "#7BC1FE",
                                                    }}
                                                  />
                                                ) : (
                                                  <EditIcon
                                                    fontSize="small"
                                                    onClick={() =>
                                                      setIsEditExpDate(true)
                                                    }
                                                    style={{
                                                      cursor: "pointer",
                                                      color: "#7BC1FE",
                                                    }}
                                                  />
                                                )
                                              ) : (
                                                ""
                                              )}
                                            </Grid>
                                            <Grid
                                              item
                                              xs={12}
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "16px",
                                                width: "100%",
                                              }}
                                            >
                                              <Grid
                                                container
                                                spacing={2}
                                                alignItems="center"
                                              >
                                                {/* Label */}
                                                <Grid item>
                                                  <Typography
                                                    component="span"
                                                    fontWeight="bold"
                                                  >
                                                    Issued by:
                                                  </Typography>
                                                </Grid>

                                                <Grid item>
                                                  {convertAgencyID(watchAgency)}{" "}
                                                  ({convertStateID(watchState)})
                                                </Grid>

                                                {isEditIssue ? (
                                                  <>
                                                    {/* State Dropdown */}
                                                    <Grid
                                                      item
                                                      xs={12}
                                                      sm={4}
                                                      md={6}
                                                      sx={{
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <Controller
                                                        name="StateID"
                                                        control={control}
                                                        rules={{
                                                          required:
                                                            "State is required",
                                                        }}
                                                        render={({
                                                          field,
                                                          fieldState,
                                                        }) => (
                                                          <Autocomplete
                                                            options={certState}
                                                            getOptionLabel={(
                                                              option
                                                            ) =>
                                                              option.label || ""
                                                            }
                                                            value={
                                                              certState.find(
                                                                (opt) =>
                                                                  opt.value ===
                                                                  field.value
                                                              ) || null
                                                            }
                                                            onChange={(
                                                              event,
                                                              newValue
                                                            ) =>
                                                              field.onChange(
                                                                newValue
                                                                  ? newValue.value
                                                                  : null
                                                              )
                                                            }
                                                            renderInput={(
                                                              params
                                                            ) => (
                                                              <TextField
                                                                {...params}
                                                                label="State"
                                                                size="small"
                                                                error={
                                                                  !!fieldState.error
                                                                }
                                                                helperText={
                                                                  fieldState
                                                                    .error
                                                                    ?.message
                                                                }
                                                              />
                                                            )}
                                                          />
                                                        )}
                                                      />
                                                    </Grid>

                                                    {/* Agency Dropdown */}
                                                    <Grid
                                                      item
                                                      xs={12}
                                                      sm={5}
                                                      md={4}
                                                      sx={{
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <Controller
                                                        name="AgencyID"
                                                        control={control}
                                                        rules={{
                                                          required:
                                                            "Agency is required",
                                                        }}
                                                        render={({
                                                          field,
                                                          fieldState,
                                                        }) => (
                                                          <Autocomplete
                                                            options={certAgency}
                                                            getOptionLabel={(
                                                              option
                                                            ) =>
                                                              option.label || ""
                                                            }
                                                            value={
                                                              certAgency.find(
                                                                (opt) =>
                                                                  opt.value ===
                                                                  field.value
                                                              ) || null
                                                            }
                                                            onChange={(
                                                              event,
                                                              newValue
                                                            ) =>
                                                              field.onChange(
                                                                newValue
                                                                  ? newValue.value
                                                                  : null
                                                              )
                                                            }
                                                            renderInput={(
                                                              params
                                                            ) => (
                                                              <TextField
                                                                {...params}
                                                                label="Agency"
                                                                size="small"
                                                                error={
                                                                  !!fieldState.error
                                                                }
                                                                helperText={
                                                                  fieldState
                                                                    .error
                                                                    ?.message
                                                                }
                                                              />
                                                            )}
                                                          />
                                                        )}
                                                      />
                                                    </Grid>
                                                  </>
                                                ) : (
                                                  ""
                                                )}
                                              </Grid>
                                              {!isUserAdmin ? (
                                                isEditIssue ? (
                                                  <Check
                                                    fontSize="small"
                                                    onClick={() =>
                                                      setIsEditIssue(false)
                                                    }
                                                    style={{
                                                      cursor: "pointer",
                                                      color: "#7BC1FE",
                                                    }}
                                                  />
                                                ) : (
                                                  <EditIcon
                                                    fontSize="small"
                                                    onClick={() =>
                                                      setIsEditIssue(true)
                                                    }
                                                    style={{
                                                      cursor: "pointer",
                                                      color: "#7BC1FE",
                                                    }}
                                                  />
                                                )
                                              ) : (
                                                ""
                                              )}
                                            </Grid>
                                          </Grid>
                                        ) : (
                                          ""
                                        )}
                                      </>
                                    ))
                                  : ""}
                              </Typography>
                            </Box>
                          )}
                          {action === "ADD" &&
                          selectedDocumentType === 4 &&
                          uploadedDocument[0].DocumentTypeID === 4 ? (
                            <Grid container spacing={2}>
                              <Grid item size={{ xs: 12 }}>
                                <Controller
                                  name="ExpiryDate"
                                  control={control}
                                  rules={{
                                    required: "Expiration date is required",
                                  }}
                                  render={({ field, fieldState }) => (
                                    <LocalizationProvider
                                      dateAdapter={AdapterDayjs}
                                    >
                                      <DatePicker
                                        label="Expires On"
                                        value={
                                          field.value
                                            ? dayjs(field.value)
                                            : null
                                        }
                                        onChange={(newValue) =>
                                          field.onChange(
                                            newValue
                                              ? newValue.toISOString()
                                              : null
                                          )
                                        }
                                        minDate={dayjs()}
                                        slotProps={{
                                          textField: {
                                            error: !!fieldState.error,
                                            helperText:
                                              fieldState.error?.message,
                                          },
                                        }}
                                      />
                                    </LocalizationProvider>
                                  )}
                                />
                              </Grid>

                              {/* Two dropdowns side‑by‑side */}
                              <Grid item size={{ xs: 12, md: 6 }}>
                                <Controller
                                  name="StateID"
                                  control={control}
                                  rules={{
                                    required: "State is required",
                                  }}
                                  render={({ field, fieldState }) => (
                                    <Autocomplete
                                      options={certState}
                                      getOptionLabel={(option) =>
                                        option.label || ""
                                      }
                                      value={
                                        certState.find(
                                          (opt) => opt.value === field.value
                                        ) || null
                                      }
                                      onChange={(event, newValue) => {
                                        field.onChange(
                                          newValue ? newValue.value : null
                                        );
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="State"
                                          fullWidth
                                          error={!!fieldState.error}
                                          helperText={fieldState.error?.message}
                                        />
                                      )}
                                    />
                                  )}
                                />
                              </Grid>

                              <Grid item size={{ xs: 12, md: 6 }}>
                                <Controller
                                  name="AgencyID"
                                  control={control}
                                  rules={{
                                    required: "Agency is required",
                                  }}
                                  render={({ field, fieldState }) => (
                                    <Autocomplete
                                      options={certAgency}
                                      getOptionLabel={(option) =>
                                        option.label || ""
                                      }
                                      value={
                                        certAgency.find(
                                          (opt) => opt.value === field.value
                                        ) || null
                                      }
                                      onChange={(event, newValue) => {
                                        field.onChange(
                                          newValue ? newValue.value : null
                                        );
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Agency"
                                          fullWidth
                                          error={!!fieldState.error}
                                          helperText={fieldState.error?.message}
                                        />
                                      )}
                                    />
                                  )}
                                />
                              </Grid>
                            </Grid>
                          ) : (
                            ""
                          )}
                        </Box>
                      );
                    } else {
                      return <></>;
                    }
                  })}
              </Typography>
            </Typography>
          </Grid>
        ) : (
          ""
        )}
      </Grid>
      {open && (
        <ModalPopup
          header={uploadLabels.header}
          message1={uploadLabels.message1}
          btnPrimaryText={uploadLabels.btnPrimaryText}
          btnSecondaryText={uploadLabels.btnSecondaryText}
          handlePrimaryClick={() => handleFileRemove(fileToRemove)}
          handleSecondaryClick={handleDialogClose}
        />
      )}
    </Typography>
  );
};

export default FileUpload;
